import React, { forwardRef, lazy, Suspense } from 'react';

import { Box } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangeKind, PendingChangesActionType } from 'constants/pendingChanges';
import { getPendingChangeWrapperState } from 'utils/pendingChangeKind';

import { getWrapperStyles } from './PendingChangeWrapper.styles';

// Loaded on first use: the dots read the loan's pending-changes store, which the many tables and pages
// that use this wrapper without authors should not pull in.
const PendingChangeAuthorDots = lazy(() => import('../PendingChangeAuthorDots'));

const PendingChangeWrapper = forwardRef(
  (
    {
      children,
      isSmall = false,
      sx,
      textSelector = 'p',
      withBackground = true,
      pendingChangesActionType: propsPendingChangesActionType,
      authors = null,
      fieldLabel = '',
      changeKind: propsChangeKind = null,
      ...props
    },
    ref,
  ) => {
    const { pendingChangesActionType, changeKind } = getPendingChangeWrapperState(
      propsPendingChangesActionType,
      propsChangeKind,
    );
    const isWithAuthors = changeKind === PendingChangeKind.DIRECT && !_.isEmpty(authors);

    return (
      <Box
        data-change-kind={changeKind}
        data-is-changed={changeKind === PendingChangeKind.DIRECT}
        data-pending-changes-action-type={pendingChangesActionType}
        ref={ref}
        sx={getWrapperStyles({
          pendingChangesActionType,
          changeKind,
          sx,
          isSmall,
          textSelector,
          withBackground,
          isWithAuthors,
        })}
        {...props}
      >
        {isWithAuthors && (
          <Suspense fallback={null}>
            <PendingChangeAuthorDots authors={authors} fieldLabel={fieldLabel} />
          </Suspense>
        )}
        {children}
      </Box>
    );
  },
);

PendingChangeWrapper.propTypes = {
  pendingChangesActionType: PropTypes.oneOf(Object.values(PendingChangesActionType)),
  textSelector: PropTypes.string,
  sx: PropTypes.object,
  isSmall: PropTypes.bool,
  withBackground: PropTypes.bool,
  authors: PropTypes.arrayOf(PropTypes.object),
  fieldLabel: PropTypes.string,
  // Why the value is pending: a Direct change is highlighted, a Recalculated one underlined. Alone it marks the
  // value modified.
  changeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
};

export default PendingChangeWrapper;
